import {Component, Input, OnInit} from '@angular/core';
import {BorderRadius, ChartConfiguration, Scale} from "chart.js";
import {BaseChart} from "../base-chart.component";
import moment, {unitOfTime} from "moment/moment";
import {DataType, TimeResolution} from "@flowmaps/flowmaps-typescriptmodels";
import {getCSSVariableValue} from '../../../../chartjs/helpers';
import {localTimeFormat} from "../../../common/utils";
import {DashboardTime, TimeChangedEvent} from "../../../utils/chart-data-provider";
import {BarAxesScalerPlugin} from "./bar-axes-scaler.plugin";
import {DateRangeUtils} from "../../../common/date/date-range/date-range.utils";
import {ActiveElement, Chart, ChartEvent} from "chart.js/dist/types";
import {BarAxesPercentagePlugin} from "./bar-axes-percentage.plugin";
import {AppContext} from "../../../app-context";
import lodash from "lodash";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {MomentDateFieldRange} from "../../../common/date/date-range/date-field-range";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {AppCommonUtils} from "../../../common/app-common-utils";
import {debounceTime, distinctUntilChanged} from "rxjs";


@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss'],
    providers: [{provide: BaseChart, useExisting: BarChartComponent}]
})
export class BarChartComponent extends BaseChart<'bar'> implements OnInit {

    possibleResolutions: TimeResolution[] = [
        TimeResolution.year,
        TimeResolution.month,
        TimeResolution.day,
        TimeResolution.hour,
        TimeResolution.minute
    ]

    @Input() stacked: boolean;
    @Input() showLegend: boolean;
    @Input() hideDefaultYAxis: boolean;
    @Input() y1AxisMeasurement: string;
    @Input() y2AxisMeasurement: string;
    @Input() powerAxisMeasurement: string;
    @Input() showNormalAxis: boolean;
    @Input() showCompleteness: boolean = false;
    @Input() animationDuration: number = 1000;
    @Input() selectedWeatherTypes: DataType[] = [];
    @Input() weatherTypes: DataType[] = [];
    @Input() horizontally: boolean;
    @Input() customOnClick: ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | boolean;
    @Input() customHover: ((event: ChartEvent, elements: ActiveElement[], chart: Chart) => void) | boolean;
    @Input() topPadding: number;
    @Input() skipPaddingX: boolean;
    @Input() otherPlugins: any[] = [];
    plugins: any[] = [];

    barAxesScalerPlugin = new BarAxesScalerPlugin(this.chartUtils);
    barAxesPercentagePlugin = new BarAxesPercentagePlugin(this.chartUtils, ["completeness", "sunHours"]);
    timeRange: DashboardTime;
    refreshOptionsOnDataChange = true;
    private _splitOffPeak = false;

    @Input()
    set splitOffPeak(value: boolean) {
        this._splitOffPeak = value ?? false;
    }

    ngOnInit() {
        this.subscribeTo("getTimeRange").pipe(debounceTime(100),
            distinctUntilChanged((prev, curr) => lodash.isEqual(prev, curr)))
            .subscribe((t: DashboardTime) => this.timeRange = t)
        this.barAxesScalerPlugin.scaleChanged.pipe(debounceTime(200),
            distinctUntilChanged((prev, curr) => lodash.isEqual(prev, curr)))
            .subscribe(scales => this.ngZone.runOutsideAngular(() => this.refreshOptions()));
        this.plugins = [
            this.barAxesScalerPlugin,
            this.barAxesPercentagePlugin,
            ...(this.otherPlugins ?? [])
        ];
    }

    getOptions(): ChartConfiguration<'bar' | 'scatter'>['options'] {
        const scales: any = this.getDefaultScales();
        this.addWeatherScales(scales);
        if (!this._splitOffPeak) {
            this.chartData.datasets.forEach(ds => {
                ds.label = ds.label?.replace(/\s*\b(?:normal|normaal)\b/i, '').trim()
            });
        }
        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: this.animationDuration
            },
            layout: {
                padding: {
                    top: this.topPadding || 0
                }
            },
            normalized: true,
            elements: {
                point: {
                    radius: 0
                },
                bar: {
                    borderRadius: this.borderRadius
                }
            },
            indexAxis: this.horizontally ? 'y' : 'x',
            scales: scales,
            plugins: {
                datalabels: {
                    display: false
                },
                tooltip: {
                    mode: this.horizontally ? "y" : "index",
                    intersect: false,
                    enabled: false,
                    callbacks: {
                        title(tooltipItems: any[]): string | string[] | void {
                            return lodash.uniq(tooltipItems.map(t => t.dataset?.tooltipLabels ? t.dataset.tooltipLabels[t.dataIndex] || t.label : t.label));
                        },
                        footer(tooltipItems: any[]): string {
                            const dataset = tooltipItems[0]?.dataset as any;
                            return dataset?.footerText;
                        }
                    },
                },
                legend: {
                    display: this.showLegend,
                    position: 'right'
                },
                title: {
                    display: true,
                    text: this.chartTitle,
                    font: {
                        size: 20
                    }
                },
                decimation: {
                    enabled: true,
                    algorithm: 'lttb',
                    samples: 500
                }
            },
            hover: {
                mode: "dataset"
            },
            onHover: this.customHover === false ? null : (this.customHover as any || this.onHover),
            onClick: this.customOnClick === false ? null : (this.customOnClick as any || this.zoomDataRange)
        }
    }

    private zoomDataRange = (evt: ChartEvent, activeEls: ActiveElement[], chart: Chart) => {
        const canZoomIn = this.timeRange.resolution != "hour";
        const hoverOverBar = activeEls.length > 0;
        const canZoomOut = this.timeRange.resolution !== TimeResolution.year;
        const points = chart.getElementsAtEventForMode(evt.native, 'nearest', {intersect: true}, true);
        const currentIndex = this.possibleResolutions.indexOf(this.timeRange.resolution);
        const currentResolution = this.possibleResolutions[currentIndex];

        if (points.length > 0) {
            const newResolution = this.possibleResolutions[currentIndex + 1];

            if (canZoomIn && hoverOverBar) {
                const startTime = moment(this.timeRange.start).add(points[0].index, currentResolution);
                this.timeChanged({
                    resolution: newResolution,
                    start: startTime.startOf(currentResolution).format(localTimeFormat),
                    end: startTime.add(1, currentResolution).startOf(currentResolution).format(localTimeFormat),
                    label: null
                }, true);
            }
        } else if (!hoverOverBar && canZoomOut) {
            this.zoomOut(currentIndex);
        }
    };

    private getDefaultScales() {
        return {
            x: {
                position: 'bottom',
                stacked: this.stacked,
                offset: !this.horizontally,
                ticks: {
                    autoSkip: !this.skipPaddingX,
                    autoSkipPadding: this.skipPaddingX ? 0 : 50,
                    minRotation: this.skipPaddingX ? 45 : 0,
                    maxRotation: this.skipPaddingX ? 45 : 0
                },
                title: {
                    display: this.horizontally,
                    text: this.barAxesScalerPlugin.axisTitle(this.y1AxisMeasurement, "x")
                }
            },
            y1: {
                display: !this.hideDefaultYAxis && !!this.showNormalAxis,
                stacked: this.stacked,
                position: 'left',
                ticks: {
                    autoSkip: true,
                },
                title: {
                    display: !!this.y1AxisMeasurement,
                    text: this.barAxesScalerPlugin.axisTitle(this.y1AxisMeasurement, "y1")
                },
                grid: {
                    color: (context, options) => {
                        if (context.tick?.value === 0) {
                            return "#969696";
                        }
                        if (context.index % 2 === 0) {
                            return getCSSVariableValue('--bs-chart-grid-line-color');
                        }
                    },
                }
            },
            y1Aggregated: {
                display: !this.hideDefaultYAxis && !!!this.showNormalAxis && !this.horizontally,
                stacked: true,
                position: 'left',
                ticks: {
                    autoSkip: true,
                },
                title: {
                    display: !!this.y1AxisMeasurement,
                    text: this.barAxesScalerPlugin.axisTitle(this.y1AxisMeasurement, "y1Aggregated")
                },
                grid: {
                    color: (context, options) => {
                        if (context.tick?.value === 0) {
                            return "rgb(101,101,101)";
                        }
                        if (context.index % 2 === 0) {
                            return getCSSVariableValue('--bs-chart-grid-line-color');
                        }
                    },
                }
            },
            y2: this.getSecondYAxes(!!this.y2AxisMeasurement && this.selectedWeatherTypes.length === 0),
            y2Previous: this.getSecondPreviousYAxes(),
            power: {
                type: 'linear',
                position: 'right',
                display: !!this.powerAxisMeasurement,
                ticks: {
                    autoSkip: true,
                },
                grid: {
                    drawOnChartArea: false
                },
                title: {
                    display: true,
                    text: this.barAxesScalerPlugin.axisTitle(this.powerAxisMeasurement, "power")
                }
            },
            lines: {
                type: 'linear',
                position: 'left',
                display: this.chartData.datasets.some(d => d.yAxisID === "lines"),
                ticks: {
                    autoSkip: true,
                },
                grid: {
                    drawOnChartArea: true
                },
                title: {
                    display: true,
                    text: this.barAxesScalerPlugin.axisTitle(this.y1AxisMeasurement, "lines")
                }
            },
            completeness: {
                type: 'linear',
                position: 'right',
                display: !!this.showCompleteness && !this.powerAxisMeasurement && !this.y2AxisMeasurement && this.selectedWeatherTypes.length === 0,
                ticks: {
                    autoSkip: true,
                },
                suggestedMin: 0,
                suggestedMax: 1,
                grid: {
                    drawOnChartArea: false
                },
                title: {
                    display: true,
                    text: this.barAxesPercentagePlugin.axisTitle(TranslateDirective.getTranslation("Completeness", true))
                }
            },
            yScatter: {
                stacked: false,
                position: 'right',
                display: false,
                grid: {
                    drawOnChartArea: false
                },
                beginAtZero: !this.chartDirective.chart.data.datasets.some(d =>
                    d.data.some(v => typeof v === 'number' && v < 0)
                ),
                ticks: {
                    autoSkip: true,
                },
            }
        };
    }

    private getSecondPreviousYAxes(): any {
        return {
            type: 'linear',
            position: 'right',
            display: false,
            ticks: {
                autoSkip: true,
            },
        };
    }

    private getSecondYAxes(display: boolean): any {
        return {
            type: 'linear',
            position: 'right',
            display: display,
            grid: {
                drawOnChartArea: false
            },
            ticks: {
                autoSkip: true,
            },
            title: {
                display: true,
                text: this.barAxesScalerPlugin.axisTitle(this.y2AxisMeasurement, "y2")
            }
        };
    }

    private zoomOut(currentIndex: number) {
        this.sendQuery("getDashboardHistory").subscribe((h: DashboardTime[]) => {
            if (h.length > 0) {
                this.timeChanged(lodash.last(h), false);
                return;
            }
            const newResolution = this.possibleResolutions[currentIndex - 1];
            if (newResolution === TimeResolution.year) {
                const allSelectedTimeRange = this.getAllSelectedTimeRange();
                this.timeChanged({
                    resolution: newResolution,
                    start: allSelectedTimeRange.start,
                    end: allSelectedTimeRange.end
                });
            } else {
                const timeResolution = this.possibleResolutions[currentIndex - 2] as unitOfTime.Base;
                this.timeChanged({
                    resolution: newResolution,
                    start: moment(this.timeRange.start).startOf(timeResolution).format(localTimeFormat),
                    end: moment(this.timeRange.start).add(1, timeResolution)
                        .startOf(timeResolution).format(localTimeFormat)
                });
            }
        });
    }

    private timeChanged(time: DashboardTime, addToHistory?: boolean) {
        this.sendQuery("getTimeRanges").subscribe((t: MomentDateFieldRange[]) => {
            time.label = DateRangeUtils.getRangeLabel({
                start: moment(time.start),
                end: moment(time.end)
            }, t);
            if (this.directiveView) {
                this.directiveView.publishEvent("timeChanged", <TimeChangedEvent>{
                    historyType: addToHistory === true ? 'add' : addToHistory === false ? 'remove' : undefined,
                    timeRange: time
                });
            }
        });
    }

    private addWeatherScales(scales: any) {
        this.weatherTypes.forEach(w => {
            const scale = this.getSecondYAxes(!!this.y2AxisMeasurement && this.selectedWeatherTypes.includes(w));
            if (w === DataType.sunHours) {
                scale.title.text = this.barAxesPercentagePlugin.axisTitle(AppContext.measurementName(w, true));
            }
            scales[`${w}`] = scale;
            scales[`${w}Previous`] = this.getSecondPreviousYAxes();
        })
    }

    borderRadius = (tick): BorderRadius => {
        if (this.horizontally) {
            if (tick.raw > 0) {
                return {
                    bottomLeft: 0,
                    bottomRight: 10,
                    topLeft: 0,
                    topRight: 10
                }
            } else {
                return {
                    bottomLeft: 10,
                    bottomRight: 0,
                    topLeft: 10,
                    topRight: 0
                }
            }
        } else {
            if (tick.raw > 0) {
                return {
                    bottomLeft: 0,
                    bottomRight: 0,
                    topLeft: 10,
                    topRight: 10
                }
            } else {
                return {
                    bottomLeft: 10,
                    bottomRight: 10,
                    topLeft: 0,
                    topRight: 0
                }
            }
        }
    }

    private onHover = (evt: ChartEvent, activeEls: ActiveElement[]) => {
        const target: any = evt.native.target;
        const canZoomIn = this.timeRange.resolution != "hour";
        const canZoomOut = this.timeRange.resolution !== TimeResolution.year;
        const hoverOverBar = activeEls.length > 0;
        if (hoverOverBar && canZoomIn) {
            target.style.cursor = "pointer";
        } else if (!hoverOverBar && canZoomOut) {
            target.style.cursor = "zoom-out";
        } else {
            target.style.cursor = "auto";
        }
    }

    private getAllSelectedTimeRange() {
        return {
            start: moment().subtract(DashboardContext.maximumYears, "year").startOf("year").format(localTimeFormat),
            end: moment().add(1, "year").startOf("year").format(localTimeFormat)
        };
    }
}

export const scatterExclusiveTooltipPlugin = {
    id: 'scatterExclusiveTooltip',
    _customOnClick() {
        AppCommonUtils.navigateToUrl("/alerts")
    },
    beforeEvent(chart, args, options) {
        const event = args.event;
        const tooltip = chart.tooltip;
        const scatterDatasets = chart.data.datasets
            .map((dataset, index) => ({dataset, index}))
            .filter(d => d.dataset.type === 'scatter');
        const scatterPointHovered = scatterDatasets.some(({index}) => {
            const meta = chart.getDatasetMeta(index);
            if (!meta || !meta.data) return false;
            return meta.data.some(point => point.inRange(event.x, event.y));
        });
        if (scatterPointHovered) {
            tooltip.options.mode = 'nearest';
            tooltip.options.intersect = true;
            tooltip.options.filter = item => item.dataset.type === 'scatter';
        } else {
            tooltip.options.mode = 'index';
            tooltip.options.intersect = false;
            tooltip.options.filter = item => item.dataset.type !== 'scatter';
        }
        if (event.type === 'click' && scatterPointHovered) {
            event.native.preventDefault();
            event.native.stopImmediatePropagation();
            this._customOnClick();
            return false;
        }
    }
};


export const syncScatterYAxisPlugin = {
    id: 'syncScatterYAxis',
    _shouldSync(chart) {
        return chart.data?.datasets?.some(ds => ds.yAxisID === 'yScatter');
    },
    _syncMinMax(yScale, yScatterScale) {
        yScatterScale.options.min = yScale.min;
        if (yScale.max > yScatterScale.max) {
            yScatterScale.options.max = yScale.max;
            yScale.options.max = yScale.max;
        } else if (yScale.max < yScatterScale.max) {
            yScale.options.max = yScatterScale.max;
            yScatterScale.options.max = yScatterScale.max;
        }
    },
    _syncTicks(yScale, yScatterScale) {
        const y1ticks = yScale.ticks;
        const yScatterTicks = yScatterScale.ticks;
        if (y1ticks?.length === yScatterTicks?.length &&
            y1ticks.every((t, i) => t.value === yScatterTicks[i].value &&
                t.scale === yScatterTicks[i].scale && t.label === yScatterTicks[i].label)) {
            return;
        }
        const step = y1ticks[1].value - y1ticks[0].value;
        const scaleFactor = y1ticks[0].scale ?? 1;
        const min = yScale.min;
        const lastTick = y1ticks[y1ticks.length - 1].value;
        const max = Math.max(
            ...[yScale.options.max, yScale.max, yScatterScale.options.max, yScatterScale.max, lastTick]
                .filter(v => v != null)
        );
        const generatedTicks = [];
        for (let v = min; v <= max; v += step) {
            generatedTicks.push({
                value: v,
                scale: scaleFactor,
                label: (v / scaleFactor).toLocaleString()
            });
        }
        yScale.ticks = generatedTicks;
        yScatterScale.ticks = [...generatedTicks];
    },
    beforeUpdate(chart) {
        if (!this._shouldSync(chart)) return;
        const yScale = chart.scales[chart.data.datasets.some(d => d.yAxisID === 'y1Aggregated') ? 'y1Aggregated' : 'y1'];
        const yScatterScale = chart.scales['yScatter'];
        this._syncMinMax(yScale, yScatterScale);
    },
    afterBuildTicks(chart,  args: { scale: Scale }) {
        if (!this._shouldSync(chart)) return;
        const yScale = chart.scales[chart.data?.datasets?.find(ds => ds.stack === 'Current period').yAxisID];
        const yScatterScale = chart.scales['yScatter'];
        if ((args.scale.id == "y1" || args.scale.id == "yScatter") && yScale.ticks?.length > 2) {
            this._syncTicks(yScale, yScatterScale);
        }
    }
};
